/**
 * Cat (Bedrock numbers): 10 HP, speed 0.3. Wild cats keep their distance from
 * the player but come to one holding raw cod or salmon; feeding one tames it
 * (1/3 per fish, the fish is used up). A tamed cat gets a red collar, starts
 * sitting, follows its owner (teleports past 12 blocks), toggles sit / stand
 * on a bare-hand right-click and heals 2 HP per fish. Creepers (and phantoms,
 * not implemented) flee from cats through `navHooks.scaredOf` (set in
 * EntityManager).
 *
 * `view.color`: bits 0-3 collar dye, bit 4 tamed, bit 5 sitting, bits 6-9 coat 0-10
 * (tabby, tuxedo, red, siamese, british shorthair, calico, persian, ragdoll, white, jellie, black).
 * Wild cats spawn only where STRUCTURES places them (villages, swamp huts): call
 * `entities.spawnMob('cat', x, y, z)`; coat 10 (black) for swamp huts via `setCoat`.
 */

import { I } from '../../game/itemIds';
import { IS_SOLID } from '../../blocks';
import type { Stack } from '../../game/types';
import { Mob, type MobCtx } from '../mob';

export const CAT_TAME_CHANCE = 1 / 3;
export const CAT_TELEPORT_DIST = 12;
// vanilla: only RAW cod / salmon tame, heal (2 HP) and breed cats; cooked fish is not cat food
export const CAT_FOOD: ReadonlyMap<number, number> = new Map([[I.COD, 2], [I.SALMON, 2]]);
const isTempt = (id: number): boolean => id === I.COD || id === I.SALMON;

export class Cat extends Mob {
  tame = false;
  sitting = false;
  collar = 14;
  coat: number;
  private tpCd = 0;

  constructor(id: number, x: number, y: number, z: number, rnd: () => number) {
    super(id, 'cat', x, y, z, { width: 0.6, height: 0.7, eye: 0.56, health: 10, speed: 0.3 }, rnd);
    this.coat = Math.floor(rnd() * 10);
  }

  setCoat(c: number): void { this.coat = Math.max(0, Math.min(10, Math.floor(c))); }

  /** Right-click with `held` (0 = empty). */
  interact(held: number, rnd: () => number): { handled: boolean; consumed: boolean } {
    if (!this.alive) return { handled: false, consumed: false };
    if (!this.tame) {
      if (!isTempt(held)) return { handled: false, consumed: false };
      if (rnd() < CAT_TAME_CHANCE) {
        this.tame = true; this.sitting = true;
        this.host?.effect('heart', this.x, this.y + this.height + 0.3, this.z);
      } else this.host?.effect('poof', this.x, this.y + this.height * 0.6, this.z);
      return { handled: true, consumed: true };
    }
    const heal = CAT_FOOD.get(held);
    if (heal !== undefined && this.health < this.maxHealth) {
      this.health = Math.min(this.maxHealth, this.health + heal);
      this.host?.effect('heart', this.x, this.y + this.height + 0.3, this.z);
      return { handled: true, consumed: true };
    }
    if (heal !== undefined) return { handled: false, consumed: false };
    this.sitting = !this.sitting;
    return { handled: true, consumed: false };
  }

  override hurt(amount: number): boolean {
    const r = super.hurt(amount);
    if (r && this.tame) this.sitting = false;
    return r;
  }

  xpValue(): number { return 0; }
  loot(rnd: () => number): Stack[] { return rnd() < 0.5 ? [{ id: I.STRING, count: 1, dmg: 0 }] : []; }

  saveExtra(): unknown { return [this.tame ? 1 : 0, this.sitting ? 1 : 0, this.collar, this.coat]; }
  loadExtra(d: unknown): void {
    const a = d as number[];
    this.tame = a?.[0] === 1;
    this.sitting = a?.[1] === 1;
    this.collar = typeof a?.[2] === 'number' ? a[2] : 14;
    this.setCoat(typeof a?.[3] === 'number' ? a[3] : 0);
  }

  private teleportToOwner(ctx: MobCtx): boolean {
    const p = ctx.host.player, w = this.w, rnd = ctx.host.rnd;
    for (let i = 0; i < 12; i++) {
      const tx = Math.floor(p.x + (rnd() * 6 - 3)), tz = Math.floor(p.z + (rnd() * 6 - 3));
      const ty = Math.floor(p.y) + Math.floor(rnd() * 3) - 1;
      if (Math.abs(tx + 0.5 - p.x) < 2 && Math.abs(tz + 0.5 - p.z) < 2) continue;
      if (IS_SOLID[w.getBlock(tx, ty - 1, tz)] === 1 && !IS_SOLID[w.getBlock(tx, ty, tz)] && !IS_SOLID[w.getBlock(tx, ty + 1, tz)]) {
        this.x = this.px = tx + 0.5; this.y = this.py = ty; this.z = this.pz = tz + 0.5;
        this.vx = this.vy = this.vz = 0;
        return true;
      }
    }
    return false;
  }

  protected ai(ctx: MobCtx): void {
    if (this.tpCd > 0) this.tpCd--;
    const p = ctx.host.player;
    const dx = p.x - this.x, dz = p.z - this.z;
    const d2 = dx * dx + dz * dz;
    if (this.tame) {
      if (this.sitting) { this.lookAtNearPlayer(ctx); return; }
      if (d2 > CAT_TELEPORT_DIST * CAT_TELEPORT_DIST && this.tpCd === 0) { this.tpCd = 20; if (this.teleportToOwner(ctx)) return; }
      if (d2 > 16) {
        this.faceToward(p.x, p.z); this.lookAt(p.x, p.eyeY, p.z);
        this.steerTo(ctx, p.x, p.y, p.z, d2 > 64 ? 1.6 : 1.2, 6);
        return;
      }
      if (d2 < 36) { this.lookAt(p.x, p.eyeY, p.z); return; }
      this.stroll(ctx, 0.6, 4, true);
      return;
    }
    // wild: tempted by fish within 10, otherwise skittish within 4
    if (ctx.host.heldItem && isTempt(ctx.host.heldItem()) && d2 < 100 && d2 > 2.25) {
      this.faceToward(p.x, p.z); this.lookAt(p.x, p.eyeY, p.z);
      this.steerTo(ctx, p.x, p.y, p.z, 1, 6);
      return;
    }
    if (d2 < 16 && p.alive && this.navAway(ctx, p.x, p.z, 1.3, 3, true)) return;
    this.stroll(ctx, 0.8, 4, true);
    this.lookAtNearPlayer(ctx);
  }

  override syncView(host: Parameters<Mob['syncView']>[0]): void {
    super.syncView(host);
    this.view.color = (this.collar & 15) | (this.tame ? 16 : 0) | (this.sitting ? 32 : 0) | (this.coat << 6);
  }
}
